import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import {
  advisorService,
  AdvisorActivity,
  AdvisorResponse,
  AdvisorResult,
} from '../services/advisorService';
import styles from './ShipAdvisor.module.css';

const fmt = (v: number) => Math.round(v).toLocaleString('en-US');

/**
 * Ship Advisor: pick up to three things you want to DO in the verse and a
 * data-only ranking shows which ships can actually do them — with the
 * receipts (each stat, its value, and where it stands fleet-wide).
 * Prices are shown for context and never scored.
 */
const ShipAdvisor: React.FC = () => {
  const [activities, setActivities] = useState<AdvisorActivity[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [buyableOnly, setBuyableOnly] = useState(false);
  const [maxCrew, setMaxCrew] = useState<number | undefined>(undefined);
  const [budget, setBudget] = useState('');
  const [budgetUsd, setBudgetUsd] = useState('');
  const [response, setResponse] = useState<AdvisorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    advisorService
      .getActivities()
      .then((acts) => setActivities(acts))
      .catch(() => setError('Could not load activities. Check your connection.'))
      .finally(() => setLoading(false));
  }, []);

  const runRank = useCallback(
    (
      picks: string[],
      buyable: boolean,
      crew?: number,
      budgetStr?: string,
      budgetUsdStr?: string
    ) => {
      if (picks.length === 0) {
        setResponse(null);
        return;
      }
      setRanking(true);
      setError(null);
      const num = (v?: string) => {
        const n = v ? Number(v.replace(/[^0-9]/g, '')) : NaN;
        return !Number.isNaN(n) && n > 0 ? n : undefined;
      };
      advisorService
        .rank({
          activities: picks,
          buyableOnly: buyable,
          maxCrew: crew,
          budgetAuec: num(budgetStr),
          budgetUsd: num(budgetUsdStr),
        })
        .then((res) => {
          setResponse(res);
          setExpanded(null);
        })
        .catch(() => setError('Ranking failed. Try again in a moment.'))
        .finally(() => setRanking(false));
    },
    []
  );

  const schedule = useCallback(
    (
      picks: string[],
      buyable: boolean,
      crew?: number,
      budgetStr?: string,
      budgetUsdStr?: string
    ) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(
        () => runRank(picks, buyable, crew, budgetStr, budgetUsdStr),
        350
      );
    },
    [runRank]
  );

  const toggle = (key: string) => {
    let next: string[];
    if (selected.includes(key)) {
      next = selected.filter((k) => k !== key);
    } else if (selected.length < 3) {
      next = [...selected, key];
    } else {
      return;
    }
    setSelected(next);
    schedule(next, buyableOnly, maxCrew, budget, budgetUsd);
  };

  const activityName = (key: string) =>
    activities.find((a) => a.key === key)?.name ?? key;

  const renderReceipts = (r: AdvisorResult) => (
    <div className={styles.receipts}>
      <div className={styles.receiptsTitle}>Why this ship</div>
      {(response?.activities ?? []).map((act) => (
        <div key={act}>
          {(response?.activities.length ?? 0) > 1 && (
            <div className={styles.receiptActivity}>{activityName(act)}</div>
          )}
          {r.receipts
            .filter((x) => x.activity === act)
            .map((rec) => (
              <div className={styles.receiptRow} key={`${act}-${rec.stat}`}>
                <div className={styles.receiptHead}>
                  <span className={styles.receiptLabel}>{rec.label}</span>
                  <span className={styles.receiptValue}>
                    {rec.display ?? 'no data'}
                  </span>
                </div>
                <div className={styles.receiptBarRow}>
                  <div className={styles.receiptBar}>
                    <div
                      className={styles.receiptBarFill}
                      style={{ width: `${Math.min(rec.percentile, 100)}%` }}
                    />
                  </div>
                  <span className={styles.receiptPct}>
                    {Math.round(rec.percentile)}th · w{rec.weight}
                  </span>
                </div>
              </div>
            ))}
        </div>
      ))}
      {r.facilities.length > 0 && (
        <div className={styles.onBoard}>
          <div className={styles.receiptActivity}>On board</div>
          <div className={styles.onBoardChips}>
            {r.facilities
              .filter((f) => f.status === 'yes')
              .map((f) => (
                <span className={styles.onBoardChip} key={f.key}>
                  {f.label}
                  {f.count > 1 ? ` ×${f.count}` : ''}
                  {f.key === 'lifeSupport' && f.detail?.startsWith('Size')
                    ? ` (${f.detail})`
                    : ''}
                </span>
              ))}
          </div>
          {r.facilities.some((f) => f.status === 'no') && (
            <div className={styles.onBoardMissing}>
              Not aboard:{' '}
              {r.facilities
                .filter((f) => f.status === 'no')
                .map((f) => f.label)
                .join(' · ')}
            </div>
          )}
          {r.facilities.some((f) => f.status === 'pending') && (
            <div className={styles.onBoardPending}>
              Not tracked yet:{' '}
              {r.facilities
                .filter((f) => f.status === 'pending')
                .map((f) => f.label)
                .join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <Sparkles size={28} /> Ship Advisor
        </h1>
        <p className={styles.subtitle}>
          Pick up to three things you want to do — the data picks the ships.
          Scores are relative to the best ship for your picks; prices are
          context, never part of the score.
        </p>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading activities…</div>
      ) : (
        <>
          <div className={styles.chips}>
            {activities.map((a) => {
              const active = selected.includes(a.key);
              const full = !active && selected.length >= 3;
              return (
                <button
                  key={a.key}
                  type="button"
                  className={`${styles.chip} ${active ? styles.chipActive : ''} ${
                    full ? styles.chipDisabled : ''
                  }`}
                  title={`${a.description} (${a.eligibleShips} ships qualify)`}
                  onClick={() => toggle(a.key)}
                >
                  {a.name}
                </button>
              );
            })}
          </div>

          <div className={styles.filters}>
            <button
              type="button"
              className={`${styles.chip} ${buyableOnly ? styles.chipActive : ''}`}
              onClick={() => {
                setBuyableOnly(!buyableOnly);
                schedule(selected, !buyableOnly, maxCrew, budget, budgetUsd);
              }}
            >
              Buyable in game
            </button>
            <select
              className={styles.select}
              value={maxCrew ?? ''}
              onChange={(e) => {
                const v = e.target.value ? Number(e.target.value) : undefined;
                setMaxCrew(v);
                schedule(selected, buyableOnly, v, budget, budgetUsd);
              }}
            >
              <option value="">Any crew</option>
              <option value="1">Solo</option>
              <option value="4">Crew ≤ 4</option>
            </select>
            <input
              className={styles.budget}
              placeholder="aUEC"
              inputMode="numeric"
              value={budget}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '');
                setBudget(v);
                schedule(selected, buyableOnly, maxCrew, v, budgetUsd);
              }}
            />
            <input
              className={styles.budget}
              placeholder="$"
              inputMode="numeric"
              value={budgetUsd}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '');
                setBudgetUsd(v);
                schedule(selected, buyableOnly, maxCrew, budget, v);
              }}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}

          {selected.length === 0 && !error && (
            <div className={styles.empty}>
              What do you want to do in Star Citizen?
              <br />
              Pick an activity above and the fleet gets ranked on data alone.
            </div>
          )}

          {selected.length > 0 && response && (
            <>
              <div className={styles.meta}>
                {response.eligibleCount} ships qualify
                {ranking && <span className={styles.metaBusy}> · updating…</span>}
              </div>
              {response.results.length === 0 && (
                <div className={styles.empty}>
                  No ship passes every gate for that combination.
                  <br />
                  That is the honest answer — try fewer activities or looser
                  filters.
                </div>
              )}
              <div className={styles.results}>
                {response.results.map((r, i) => {
                  const isOpen = expanded === r.slug;
                  return (
                    <div
                      key={r.slug}
                      className={`${styles.card} ${isOpen ? styles.cardOpen : ''}`}
                    >
                      <div className={styles.cardMain}>
                        <span
                          className={`${styles.rank} ${i < 3 ? styles.rankTop : ''}`}
                        >
                          {i + 1}
                        </span>
                        <Link
                          to={`/ship-database/${r.slug}`}
                          className={styles.thumbLink}
                        >
                          {r.imageUrl ? (
                            <img
                              src={r.imageUrl}
                              alt={r.name}
                              className={styles.thumb}
                              loading="lazy"
                            />
                          ) : (
                            <div className={styles.thumbEmpty} />
                          )}
                        </Link>
                        <div className={styles.info}>
                          <Link
                            to={`/ship-database/${r.slug}`}
                            className={styles.name}
                          >
                            {r.name}
                          </Link>
                          <div className={styles.sub}>
                            {[r.manufacturer, r.sizeClass, r.crewMax ? `crew ${r.crewMax}` : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                          <div className={styles.price}>
                            {[
                              r.priceAuec ? `${fmt(r.priceAuec)} aUEC` : null,
                              r.priceUsd ? `$${fmt(r.priceUsd)}` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ') || 'no price data'}
                          </div>
                        </div>
                        <div className={styles.scoreBox}>
                          <div className={styles.score}>{Math.round(r.score)}</div>
                          {response.activities.length > 1 && (
                            <div className={styles.split}>
                              {response.activities
                                .map(
                                  (a) =>
                                    `${activityName(a).split(' ')[0]} ${Math.round(
                                      r.activityScores[a] ?? 0
                                    )}`
                                )
                                .join(' · ')}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className={styles.expand}
                          aria-label={isOpen ? 'Hide receipts' : 'Why this ship'}
                          onClick={() => setExpanded(isOpen ? null : r.slug)}
                        >
                          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>
                      {isOpen && renderReceipts(r)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ShipAdvisor;
